import { Hammer, MessagesSquare, Lightbulb, MapPin, Phone } from "lucide-react";
import { SEO } from "@/components/SEO";
import {
  Section,
  SectionHeading,
  Breadcrumb,
  InfoGrid,
} from "@/components/Sections";
import { Reveal } from "@/components/motion";
import { CTAButton } from "@/components/CTAButton";
import { BUSINESS } from "@/lib/site";

const VALUES = [
  { icon: "Hammer", title: "Professional Workmanship", text: "Careful, professional work with attention to technical performance and the finished result." },
  { icon: "MessagesSquare", title: "Clear Communication", text: "Straightforward information about the work required, what we find and what is being carried out." },
  { icon: "Lightbulb", title: "Practical Approach", text: "Service and recommendations based on the actual condition and requirements of the air-conditioning system." },
  { icon: "MapPin", title: "Reliable Local Service", text: "Dependable air-conditioning service for homes and businesses across Paphos and surrounding areas." },
];
const ICONS = { Hammer, MessagesSquare, Lightbulb, MapPin };

export default function About() {
  return (
    <>
      <SEO
        title="About NorthWork Cyprus | Air Conditioning in Paphos"
        description="NorthWork Cyprus is an independent air-conditioning service company in Paphos, focused on professional workmanship, straightforward advice and dependable service."
        path="/about"
      />
      <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "About" }]} />

      {/* Hero — company intro, no photo */}
      <section data-testid="page-hero" className="border-b border-border bg-white">
        <div className="container-x py-12 lg:py-16">
          <div className="max-w-4xl">
            <Reveal delay={0.05} variant="up">
              <h1 className="font-heading text-4xl font-medium leading-[1.05] tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
                Professional Air Conditioning<br /> Service in Paphos
              </h1>
            </Reveal>
            <Reveal delay={0.1} variant="up" className="mt-6 max-w-3xl space-y-4">
              <p className="text-base leading-relaxed text-charcoal-muted lg:text-lg">
                NorthWork Cyprus is a local air-conditioning service company providing professional
                installation, deep cleaning, diagnostics, repairs and refrigerant services across
                Paphos and surrounding areas.
              </p>
              <p className="text-base leading-relaxed text-charcoal-muted lg:text-lg">
                We work with homes, apartments and businesses, with a focus on professional
                workmanship, clear communication and dependable service from the first inspection
                through to completion.
              </p>
            </Reveal>
            <Reveal delay={0.15} variant="up" className="mt-8 flex flex-col gap-3 sm:flex-row">
              <CTAButton to="/contact" testid="hero-primary-cta">
                Contact NorthWork
              </CTAButton>
              <CTAButton href={`tel:${BUSINESS.phoneTel}`} variant="secondary" icon={Phone} testid="hero-call-cta">
                {BUSINESS.phoneDisplay}
              </CTAButton>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Our approach */}
      <Section className="py-16 lg:py-20">
        <div className="container-x grid gap-8 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionHeading title="A Practical Approach to Air Conditioning" />
          </div>
          <Reveal delay={0.1} className="space-y-5 text-base leading-relaxed text-charcoal-muted lg:col-span-7 lg:text-lg">
            <p>
              Every property and every air-conditioning system is different. NorthWork Cyprus
              approaches each job individually, starting with understanding what is required before
              carrying out the appropriate work.
            </p>
            <p>
              We focus on completing the work properly, explaining what we find clearly and paying
              attention to both system performance and the finished appearance of the installation or
              service.
            </p>
            <p className="text-charcoal">
              Straightforward service, careful workmanship and clear communication are at the centre
              of how we work.
            </p>
          </Reveal>
        </div>
      </Section>

      {/* Values */}
      <Section className="border-t border-border bg-surface py-16 lg:py-20">
        <div className="container-x">
          <SectionHeading title="Our Values" />
          <div className="mt-10">
            <InfoGrid items={VALUES} columns={4} iconMap={ICONS} compact testid="values-grid" />
          </div>
        </div>
      </Section>

      {/* Dark CTA — no photo, blends into footer */}
      <section
        data-testid="about-cta"
        className="relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #0B111B 0%, #111B29 55%, #07101A 100%)" }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-24 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full bg-brand/10 blur-3xl"
        />
        <div className="container-x relative py-16 lg:py-20">
          <div className="max-w-3xl">
            <Reveal>
              <h2 className="font-heading text-3xl font-medium tracking-tight text-white sm:text-4xl lg:text-5xl">
                Need Air Conditioning Service?
              </h2>
            </Reveal>
            <Reveal delay={0.08} className="mt-5 max-w-2xl">
              <p className="text-base leading-relaxed text-white/70 lg:text-lg">
                Contact NorthWork Cyprus for professional air-conditioning installation, deep
                cleaning, diagnostics, repairs and refrigerant services across Paphos and surrounding
                areas.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="mt-8 flex flex-col gap-3 sm:flex-row">
              <CTAButton href={`tel:${BUSINESS.phoneTel}`} icon={Phone} testid="about-cta-call">
                Call {BUSINESS.phoneDisplay}
              </CTAButton>
              <CTAButton to="/contact" variant="outlineLight" testid="about-cta-contact">
                Contact NorthWork
              </CTAButton>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
